// react
import { useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';

// material ui
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

// api
import type { ReadGatewayDto } from '@/api/generated/recalc/endpoints/v1/gateways';
import {
  useGatewaysDeleteImageAsyncByidMutation,
  useGatewaysPatchAsyncByidgatewayMutation,
} from '@/api/generated/recalc/endpoints/v1/gateways';
import { useGatewaysPutImageAsyncByidfileMutation } from '@/api/generated/recalc/endpoints/v1/gateways.enhanced';

// shared components
import LocationMap from '@/components/maps/LocationMap';
import { CancelButton, SubmitButton } from '@/components/ui/button';
import {
  EMPTY_ADDRESS_VALUE,
  RHFAddressAutocomplete,
  RHFTextField,
} from '@/components/ui/form';

// feature-components
import DeviceTabs from '@/features/devices/components/shared/DeviceTabs';
import GatewayDetailHeader from '@/features/gateways/components/GatewayDetailHeader';
import GatewayImageSection from '@/features/gateways/components/GatewayImageSection';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import {
  toEditGatewayFormValues,
  toPatchGatewayPayload,
} from './editGatewayForm.mappers';
import { createEditGatewayFormSchema } from './editGatewayForm.schema';

import { zodResolver } from '@hookform/resolvers/zod';

const GATEWAY_TAB = {
  GENERAL: 0,
  MAP: 1,
  IMAGE: 2,
};

const EditGatewayForm = ({
  gateway,
  refetchGateway,
}: {
  gateway: ReadGatewayDto;
  refetchGateway: () => void;
}) => {
  const { t } = useTranslation([
    'forms',
    'units-gateways-sensors',
    'validation',
  ]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [patchGateway, { isLoading: isSaving }] =
    useGatewaysPatchAsyncByidgatewayMutation();
  const [uploadGatewayImage, { isLoading: isUploading }] =
    useGatewaysPutImageAsyncByidfileMutation();
  const [deleteGatewayImage, { isLoading: isDeletingImage }] =
    useGatewaysDeleteImageAsyncByidMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasImage = !!gateway.ImageHash;

  const schema = createEditGatewayFormSchema(t);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toEditGatewayFormValues(gateway),
    mode: 'onTouched',
  });

  const name = useWatch({ control, name: 'name' });
  const address = useWatch({ control, name: 'address' });
  const hasCoords = address.latitude != null && address.longitude != null;

  const handleClearLocation = () => {
    setValue('address', EMPTY_ADDRESS_VALUE, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !gateway?.GatewayId) return;
    const result = await uploadGatewayImage({
      id: gateway.GatewayId,
      body: { file },
    });
    if (!('error' in result)) {
      refetchGateway();
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = async () => {
    const result = await deleteGatewayImage({ id: gateway.GatewayId });
    if (!('error' in result)) {
      refetchGateway();
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const onSubmit = handleSubmit(async (values) => {
    const payload = toPatchGatewayPayload(values, gateway.GatewayId);
    const result = await patchGateway(payload);

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('gateways.updateSuccess', {
            ns: 'units-gateways-sensors',
          }),
          severity: 'success',
        }),
      );
      navigate({ to: '..' });
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  });

  const [activeTab, setActiveTab] = useState(GATEWAY_TAB.GENERAL);

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <GatewayDetailHeader gateway={gateway} />
        <DeviceTabs
          activeTab={activeTab}
          onTabChange={(_, newValue) => setActiveTab(newValue)}
          tabs={[
            {
              label: t('gateways.gatewayInformation', {
                ns: 'units-gateways-sensors',
                defaultValue: 'General',
              }),
              testId: 'gateway-tab-general',
            },
            {
              label: t('gateways.gatewayLocation', {
                ns: 'units-gateways-sensors',
                defaultValue: 'Location',
              }),
              testId: 'gateway-tab-map',
              icon: <LocationOnRoundedIcon fontSize="small" />,
            },
            {
              label: t('gateways.gatewayImage', {
                ns: 'units-gateways-sensors',
                defaultValue: 'Image',
              }),
              testId: 'gateway-tab-image',
              icon: <ImageRoundedIcon fontSize="small" />,
            },
          ]}
        />
        {activeTab === GATEWAY_TAB.IMAGE ? (
          <Stack spacing={2}>
            <Card variant="outlined">
              <GatewayImageSection gateway={gateway} t={t} />
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </Card>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {hasImage && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleRemoveImage}
                  disabled={isUploading || isDeletingImage}
                  startIcon={
                    isDeletingImage ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteOutlineRoundedIcon />
                    )
                  }
                >
                  {t('common.removeImage', { ns: 'units-gateways-sensors' })}
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isDeletingImage}
                startIcon={
                  isUploading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <FileUploadRoundedIcon />
                  )
                }
              >
                {hasImage
                  ? t('common.replaceImage', { ns: 'units-gateways-sensors' })
                  : t('common.uploadImage', { ns: 'units-gateways-sensors' })}
              </Button>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {activeTab === GATEWAY_TAB.GENERAL && (
              <Stack spacing={2}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <RHFTextField
                        name="name"
                        control={control}
                        required
                        fullWidth
                        label={t('editGateway.name.label', { ns: 'forms' })}
                        placeholder={t('editGateway.name.placeholder', {
                          ns: 'forms',
                        })}
                        helperText={t('editGateway.name.helperText', {
                          ns: 'forms',
                        })}
                        disabled={isSaving}
                      />

                      <RHFTextField
                        name="description"
                        control={control}
                        fullWidth
                        multiline
                        rows={2}
                        label={t('editGateway.description.label', {
                          ns: 'forms',
                        })}
                        placeholder={t('editGateway.description.placeholder', {
                          ns: 'forms',
                        })}
                        helperText={t('editGateway.description.helperText', {
                          ns: 'forms',
                        })}
                        disabled={isSaving}
                      />
                    </Stack>
                  </CardContent>
                </Card>
                <Box
                  sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
                >
                  <CancelButton
                    onClick={() => navigate({ to: '..' })}
                    disabled={isSaving}
                  >
                    {t('common.cancel')}
                  </CancelButton>
                  <SubmitButton
                    loading={isSaving}
                    disabled={isSaving || !isValid}
                    data-tour="gateway-edit-save-general"
                  >
                    {t('common.save')}
                  </SubmitButton>
                </Box>
              </Stack>
            )}

            {activeTab === GATEWAY_TAB.MAP && (
              <Stack spacing={2}>
                <Card variant="outlined">
                  {hasCoords ? (
                    <LocationMap
                      zoom={15}
                      pins={[
                        {
                          latitude: address.latitude as number,
                          longitude: address.longitude as number,
                          label: name || gateway.Name,
                        },
                      ]}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 400,
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: 'text.disabled',
                      }}
                    >
                      <RoomOutlinedIcon sx={{ fontSize: 40 }} />
                      <Typography variant="body2">
                        {t('gateways.noLocationSet', {
                          ns: 'units-gateways-sensors',
                        })}
                      </Typography>
                      <Typography variant="caption">
                        {t('gateways.searchAddressHint', {
                          ns: 'units-gateways-sensors',
                        })}
                      </Typography>
                    </Box>
                  )}
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <RHFAddressAutocomplete
                      name="address"
                      control={control}
                      required
                      disabled={isSaving}
                      fullWidth
                      size="small"
                    />
                  </CardContent>
                </Card>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Button
                    variant="text"
                    color="error"
                    onClick={handleClearLocation}
                    disabled={isSaving || !hasCoords}
                    sx={{
                      visibility: hasCoords ? 'visible' : 'hidden',
                    }}
                  >
                    {t('gateways.clearLocation', {
                      ns: 'units-gateways-sensors',
                    })}
                  </Button>
                  <Box gap={1} display="flex">
                    <CancelButton
                      onClick={() => navigate({ to: '..' })}
                      disabled={isSaving}
                    >
                      {t('common.cancel')}
                    </CancelButton>
                    <SubmitButton
                      loading={isSaving}
                      disabled={isSaving || !isValid}
                    >
                      {t('common.save')}
                    </SubmitButton>
                  </Box>
                </Box>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </form>
  );
};

export default EditGatewayForm;
